import { Order } from "@olagg/db-types";
import {
  Button,
  Text,
  Badge,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Image,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Icon,
  useClipboard,
  Tooltip
} from "@chakra-ui/react";
import { FaUser, FaShoppingCart, FaDollarSign, FaClock, FaReceipt, FaCopy } from "react-icons/fa";


const OrderDetailModal = ({ order, isOpen, onClose }: { order: Order | null, isOpen: boolean, onClose: () => void }) => {
  if (!order) return null;

  const formatDate = (dateString: Date) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Función para formatear montos basado en decimales
  const formatAmount = (amount: number, decimals: number, currency: string) => {
    if (amount === 0 || amount === null || amount === undefined) return `$0 ${currency}`;

    // Si decimals es mayor a 0, dividir por 10^decimals
    const formattedAmount = decimals > 0 ? amount / Math.pow(10, decimals) : amount;

    // Mostrar con hasta 8 decimales para precision, eliminando zeros innecesarios
    return `$${formattedAmount.toFixed(8).replace(/\.?0+$/, '')} ${currency}`;
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      'success': { bg: '#38A169', text: 'Completada' },
      'pending': { bg: '#D69E2E', text: 'Pendiente' },
      'processing': { bg: '#3182CE', text: 'Procesando' },
      'error': { bg: '#E53E3E', text: 'Error' },
      'for_revision': { bg: '#ED8936', text: 'En Revisión' },
      'refunded': { bg: '#805AD5', text: 'Reembolsada' }
    };

    const statusInfo = statusColors[status] || { bg: '#718096', text: status };

    return (
      <Badge
        bg={statusInfo.bg}
        color="white"
        fontSize="sm"
        px={3}
        py={1}
        borderRadius="md"
      >
        {statusInfo.text}
      </Badge>
    );
  };

  // Componente para mostrar JSON con botón de copiar - SOLO para rawData
  const RawDataDisplay = ({ data }: { data: any }) => {
    const jsonString = JSON.stringify(data, null, 2);
    const { hasCopied, onCopy } = useClipboard(jsonString);

    return (
      <Box mt={4}>
        <HStack justify="space-between" mb={2}>
          <Text color="gray.300" fontWeight="semibold">Datos Raw de la Transacción:</Text>
          <Tooltip label={hasCopied ? "¡Copiado!" : "Copiar JSON"}>
            <Button
              size="xs"
              onClick={onCopy}
              leftIcon={<Icon as={FaCopy} />}
              colorScheme={hasCopied ? "green" : "blue"}
              variant="outline"
            >
              {hasCopied ? "¡Copiado!" : "Copiar"}
            </Button>
          </Tooltip>
        </HStack>
        <Box
          p={3}
          bg="gray.900"
          borderRadius="md"
          maxH="300px"
          overflowY="auto"
          border="1px solid"
          borderColor="gray.600"
        >
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="green.300"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {jsonString}
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxH="90vh">
        <ModalHeader>
          <HStack>
            <Icon as={FaReceipt} />
            <Text>Detalle de Orden #{order.id}</Text>
            {order?.origin && (
              <Badge
                bg={
                  order.origin === 'lemon' ? 'green.500' :
                  order.origin === 'world' ? 'blue.500' :
                  order.origin === 'web' ? 'purple.500' :
                  'gray.500'
                }
                color="white"
                fontSize="sm"
                px={2}
                py={1}
                borderRadius="md"
              >
                {order.origin}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">

            {/* Información General de la Orden */}
            <Box p={4} bg="gray.700" borderRadius="md">
              <HStack justify="space-between" mb={4}>
                <Text fontSize="lg" fontWeight="bold">
                  <Icon as={FaShoppingCart} mr={2} />
                  Información General
                </Text>
                {getStatusBadge(order.status)}
              </HStack>

              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text color="gray.300">ID de Orden:</Text>
                  <Code colorScheme="blue">{order.id}</Code>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Monto:</Text>
                  <Text fontWeight="bold" color="green.300">
                    {formatAmount(parseFloat(order.amount), 0, order.currency)}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Descuento:</Text>
                  <Text fontWeight="bold" color="green.300">
                    {`${order.discount?.type}: ${formatAmount(parseFloat(order.discountAmount), 0, order.currency)}`}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Total con descuento:</Text>
                  <Text fontWeight="bold" color="green.300">
                    {formatAmount(parseFloat(order.totalWithDiscount), 0, order.currency)}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Tipo de Claim:</Text>
                  <Badge colorScheme="purple">{order.claimType}</Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Decimales:</Text>
                  <Text>{order.decimals}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Creada:</Text>
                  <Text>{formatDate(order.createdAt)}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Última Actualización:</Text>
                  <Text>{formatDate(order.updatedAt)}</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Información del Usuario */}
            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                <Icon as={FaUser} mr={2} />
                Información del Usuario
              </Text>

              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text color="gray.300">ID de Usuario:</Text>
                  <Code colorScheme="blue">{order.user?.id}</Code>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Nombre:</Text>
                  <Text>{order.user?.name || 'No especificado'}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Email:</Text>
                  <Text>{order.user?.email}</Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Verificado:</Text>
                  <Badge colorScheme={order.user?.verified ? 'green' : 'red'}>
                    {order.user?.verified ? 'Sí' : 'No'}
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Nivel:</Text>
                  <Text>
                    Nivel {order.user?.level?.value}
                    ({order.user?.level?.score}/{order.user?.level?.nextLevelScore} puntos)
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text color="gray.300">Roles:</Text>
                  <HStack>
                    {order.user?.roles?.map((role, index) => (
                      <Badge key={index} colorScheme="purple" size="sm">
                        {role}
                      </Badge>
                    ))}
                  </HStack>
                </HStack>
              </VStack>
            </Box>

            {/* Información del Producto */}
            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                <Icon as={FaDollarSign} mr={2} />
                Información del Producto
              </Text>

              <HStack align="start" spacing={4}>
                {order.product?.image && (
                  <Image
                    src={order.product.image}
                    alt={order.product.name}
                    boxSize="100px"
                    borderRadius="md"
                    objectFit="cover"
                  />
                )}

                <VStack flex={1} spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text color="gray.300">ID del Producto:</Text>
                    <Code colorScheme="green">{order.product?.id}</Code>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Nombre:</Text>
                    <Text fontWeight="bold">{order.product?.name}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Categoría:</Text>
                    <Badge colorScheme="blue">{order.product?.category}</Badge>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Región:</Text>
                    <Text>{order.product?.redeemableRegion}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Valor Nominal:</Text>
                    <Text>{order.product?.faceValue}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Precio Merchant:</Text>
                    <Text>${order.product?.merchantPrice} {order.product?.merchantCurrency}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Precio de Venta:</Text>
                    <Text fontWeight="bold" color="green.300">
                      ${order.product?.sellingPrice} {order.product?.sellingCurrency}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Markup:</Text>
                    <Text>{order.product?.markupPercentage}%</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">Referencia Bonox:</Text>
                    <Code colorScheme="orange">{order.product?.referenceId}</Code>
                  </HStack>

                  <HStack justify="space-between">
                    <Text color="gray.300">En Stock:</Text>
                    <Badge colorScheme={order.product?.inStock ? 'green' : 'red'}>
                      {order.product?.inStock ? 'Disponible' : 'Agotado'}
                    </Badge>
                  </HStack>
                </VStack>
              </HStack>

              {order.product?.instructions && (
                <Box mt={4} p={3} bg="gray.600" borderRadius="md">
                  <Text color="gray.300" mb={2}>Instrucciones:</Text>
                  <Text fontSize="sm">{order.product.instructions}</Text>
                </Box>
              )}

              {order.product?.description && (
                <Box mt={4} p={3} bg="gray.600" borderRadius="md">
                  <Text color="gray.300" mb={2}>Descripción:</Text>
                  <Text fontSize="sm">{order.product.description}</Text>
                </Box>
              )}
            </Box>

            {/* Transacciones */}
            {order.transactions && order.transactions.length > 0 && (
              <Box p={4} bg="gray.700" borderRadius="md">
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  <Icon as={FaClock} mr={2} />
                  Transacciones ({order.transactions.length})
                </Text>

                <Accordion allowMultiple>
                  {order.transactions.map((transaction, index) => (
                    <AccordionItem key={index} border="1px solid" borderColor="gray.600">
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <HStack>
                            <Text>Transacción #{transaction.id}</Text>
                            <Badge
                              colorScheme={transaction.status === 'success' ? 'green' : 'red'}
                              ml={2}
                            >
                              {transaction.status}
                            </Badge>
                            <Badge colorScheme="blue" ml={2}>
                              {transaction.type}
                            </Badge>
                            {/* Mostrar código de canje si existe */}
                            {transaction.rawData?.redeemCode && (
                              <Badge colorScheme="green" ml={2}>
                                ✓ Con Código
                              </Badge>
                            )}
                          </HStack>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>

                      <AccordionPanel pb={4}>
                        <VStack spacing={3} align="stretch">
                          <HStack justify="space-between">
                            <Text color="gray.300">Transaction ID:</Text>
                            <Code colorScheme="blue" fontSize="sm">{transaction.txId}</Code>
                          </HStack>

                          <HStack justify="space-between">
                            <Text color="gray.300">Estado:</Text>
                            <Badge colorScheme={transaction.status === 'success' ? 'green' : 'red'}>
                              {transaction.status}
                            </Badge>
                          </HStack>

                          {transaction.substatus && (
                            <HStack justify="space-between">
                              <Text color="gray.300">Sub-estado:</Text>
                              <Text>{transaction.substatus}</Text>
                            </HStack>
                          )}

                          <HStack justify="space-between">
                            <Text color="gray.300">Monto:</Text>
                            <Text fontWeight="bold" color="green.300">
                              {formatAmount(transaction.amount, transaction.decimals, transaction.currency)}
                            </Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text color="gray.300">Completada:</Text>
                            <Text>{transaction.completedAt ? formatDate(transaction.completedAt) : 'No completada'}</Text>
                          </HStack>

                          <HStack justify="space-between">
                            <Text color="gray.300">Creada:</Text>
                            <Text>{formatDate(transaction.createdAt)}</Text>
                          </HStack>

                          {/* Código de canje destacado */}
                          {transaction.rawData?.redeemCode && (
                            <Box p={3} bg="green.900" borderRadius="md" border="2px solid" borderColor="green.500">
                              <HStack justify="space-between">
                                <Text color="green.300" fontWeight="bold">🎟️ Código de Canje:</Text>
                                <Code colorScheme="green" fontSize="lg" fontWeight="bold">
                                  {transaction.rawData.redeemCode}
                                </Code>
                              </HStack>
                            </Box>
                          )}

                          {/* JSON solo para rawData de la transacción */}
                          {transaction.rawData && Object.keys(transaction.rawData).length > 0 && (
                            <RawDataDisplay data={transaction.rawData} />
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OrderDetailModal;